import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";

export default function WorkCompletionPrint() {
  const { id } = useParams();
  const nav = useNavigate();
  const [w, setW] = useState(null);
  const [co, setCo] = useState(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("mrm_token") : null;

  useEffect(() => {
    api.get(`/wcr/${id}`).then(({ data }) => setW(data));
    api.get("/settings/company").then(({ data }) => setCo(data)).catch(() => {});
  }, [id]);

  if (!w) return <div className="p-10 text-center text-slate-400">Loading...</div>;

  const asset = (p) => p ? (p.startsWith("data:") || p.startsWith("http") || p.startsWith("/") ? p : `${API}/files/${p}?auth=${token}`) : null;
  const val = (v) => (v && String(v).trim()) || "..........";

  return (
    <div className="min-h-screen bg-slate-100 py-6 tally-print">
      <div className="no-print max-w-4xl mx-auto flex justify-between items-center mb-4 px-4">
        <Button variant="outline" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => window.print()} data-testid="print-wcr-btn"><Printer className="w-4 h-4 mr-1" />Print / Save PDF</Button>
      </div>

      <div className="max-w-4xl mx-auto bg-white shadow-lg print-page p-10" data-testid="wcr-doc" style={{ fontFamily: '"Times New Roman", serif', fontSize: 13 }}>
        {co?.logo_url && (
          <div className="flex items-center justify-center mb-3">
            <img src={co.logo_url.startsWith("http") || co.logo_url.startsWith("/") || co.logo_url.startsWith("data:") ? co.logo_url : `${API}/files/${co.logo_url}?auth=${token}`} alt="" className="h-14 object-contain" />
          </div>
        )}
        <h1 className="text-center text-2xl font-bold underline mb-6">Work Completion Report</h1>

        <div className="mb-4">
          <div>To,</div>
          <div className="ml-4 font-semibold">{val(w.aee_office)},</div>
          <div className="ml-4 font-semibold">{val(w.discom_name)}</div>
        </div>
        <div className="mb-3">Sir/Madam,</div>
        <div className="mb-3">
          <b>Subject:</b> Submission of Work Completion Report of <b>{val(w.kw_capacity)} KW {w.meter_phase || "Single Phase"} ROOFTOP SOLAR SYSTEM.</b>
        </div>
        <div className="mb-4 border border-slate-300 rounded p-3 bg-slate-50">
          <div><b>Address:</b> {val(w.system_address)}, {val(w.customer_city)}</div>
          <div><b>Customer:</b> {val(w.customer_name)}</div>
          <div><b>(Raj) K No:</b> {val(w.k_number)} &nbsp;&nbsp; <b>A/c No:</b> {val(w.ac_number)}</div>
        </div>
        <p className="mb-4 leading-relaxed">
          Please find enclosed the work completion report of the above solar rooftop PV system installed at the address mentioned above, under net metering arrangement, as per the specifications and instructions of the {val(w.discom_name)}.
        </p>

        <Sect n="1" title="Details of the Solar PV Module">
          <Kv items={[["Total Capacity in KWp", w.pv_total_capacity_kwp], ["Make / Manufacturer", w.pv_make], ["Model No.", w.pv_model_no], ["No. of Modules", w.pv_num_modules], ["Capacity of each Module (KWp)", w.pv_each_capacity_kwp], ["Date of Installation", fmtDate(w.installation_date)], ["Date of Commissioning", fmtDate(w.commissioning_date)]]} />
        </Sect>
        <Sect n="2" title="Details of Inverter">
          <Kv items={[["Manufacturer", w.inv_make], ["Brand", w.inv_brand], ["Model No.", w.inv_model_no], ["Capacity (KWp)", w.inv_capacity_kwp], ["Serial No.", w.inv_serial_no], ["Date of Installation", fmtDate(w.inv_installation_date)], ["Date of Commissioning", fmtDate(w.inv_commissioning_date)]]} />
        </Sect>
        <Sect n="3" title="DC Cables">
          <Kv items={[["Make", w.dc_cable_make], ["Size & Type", w.dc_cable_size_type]]} />
        </Sect>
        <Sect n="4" title="AC Cables">
          <Kv items={[["Make", w.ac_cable_make], ["Size & Type", w.ac_cable_size_type]]} />
        </Sect>
        <Sect n="5" title="DC Distribution Box">
          <Kv items={[["Make", w.dcdb_make], ["Serial No", w.dcdb_serial_no], ["DC Surge Protection Device", w.dcdb_spd], ["MCB Quantity & Capacity", w.dcdb_mcb], ["Size & Type", w.dcdb_size_type]]} />
        </Sect>
        <Sect n="6" title="AC Distribution Box">
          <Kv items={[["Make", w.acdb_make], ["Serial No", w.acdb_serial_no], ["AC Surge Protection Device", w.acdb_spd], ["MCB Quantity & Capacity", w.acdb_mcb], ["Size & Type", w.acdb_size_type]]} />
        </Sect>
        <Sect n="7" title="Earthing">
          <Kv items={[["Earth Resistance (<5Ω)", w.earth_resistance], ["Earth Wire/Flat Size", w.earth_wire_size], ["Three Separate Earthing Points", w.earth_three_points], ["Size & Type", w.earth_size_type]]} />
        </Sect>
        <Sect n="8" title="Bi-directional Meter & CTs">
          <Kv items={[["Make/Manufacturer", w.meter_make], ["Serial No.", w.meter_serial_no], ["Capacity", w.meter_capacity], ["Type / Model", w.meter_type], ["Single / Three Phase", w.meter_phase], ["CT Rated", w.meter_ct_rated], ["CT Serial No.", w.meter_ct_serial], ["Date of Test by MT DESCOM", fmtDate(w.meter_test_date)]]} />
        </Sect>
        <Sect n="9" title="Details of the Caution Signs">
          <div className="pl-2">{val(w.caution_signs)}</div>
        </Sect>
        <Sect n="10" title="Provision of Manual and Automatic Switches">
          <div className="pl-2">{val(w.manual_auto_switches)}</div>
        </Sect>
        <Sect n="11" title="G.P.S. Co-ordinates of the SRTPV Installation">
          <div className="pl-2">{val(w.gps_coordinates)}</div>
          <div className="pl-2"><b>(i) Latitude:</b> {val(w.gps_lat)} &nbsp; <b>(ii) Longitude:</b> {val(w.gps_lng)}</div>
        </Sect>
        <Sect n="12" title="Operations & Maintenance Information handed over">
          <div className="pl-2">{val(w.om_info)}</div>
        </Sect>

        <p className="mt-6 leading-relaxed">
          I hereby certify that the <b>{val(w.kw_capacity)} KW</b> SRTPV system has been installed as per the technical and safety standards prescribed by {val(w.discom_name)}. Kindly arrange for the test report of the bi-directional meter and further necessary action.
        </p>

        <div className="mt-12 text-right">
          {w.created_by_signature && <img src={asset(w.created_by_signature)} alt="Signature" className="h-16 object-contain ml-auto mb-1" data-testid="wcr-signature" />}
          <div className="inline-block border-t border-black px-8 pt-1 font-semibold" data-testid="wcr-installer-name">{w.installer_name || w.created_by_name || co?.company_name || "-"}</div>
          <div className="text-xs text-slate-600 mt-1">{w.installer_designation || "Authorised Signatory"}</div>
          <div className="text-xs mt-2 italic">Seals & Signature of the System Installer</div>
        </div>
      </div>
    </div>
  );
}

function Sect({ n, title, children }) {
  return (
    <div className="my-3">
      <div className="font-bold underline">{n}. {title}:</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Kv({ items }) {
  const val = (v) => (v && String(v).trim()) || "..........";
  return (
    <table className="w-full border-collapse text-sm">
      <tbody>
        {items.map(([label, value], i) => (
          <tr key={i} className="border-b border-slate-200 last:border-b-0">
            <td className="py-1 pr-3 w-1/2 align-top">{label}</td>
            <td className="py-1 font-semibold align-top">: {val(value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
